import type { StringMap } from 'corgi-types';

import { useMatchesViewport } from 'common/hooks/media';
import type { Product } from 'common/types/product-card';
import { leastCommonMultiple } from 'common/utils/math';
import {
  COLUMNS_COUNT_M,
  COLUMNS_COUNT_S,
  LIST_VIEW_TEASER_POSITION,
  PREFERRED_RIBBON_POSITION,
  RELATED_SEARCH_POSITION_L,
  RELATED_SEARCH_POSITION_M,
  RELATED_SEARCH_POSITION_S,
  RIBBON_POSITION_L,
  RIBBON_POSITION_S,
} from '../components/constants';

interface GetPageNumberForIndexArgs {
  index: number;
  articlesCount: number;
  page?: number;
  pageSize?: number;
}

interface GetCnstrAttrsOptions {
  browsedByValue?: string;
  browsedBy?: string;
  totalCount: number;
  isSearchMode: boolean;
  resultId?: string;
}

export const getPageNumberForIndex = ({
  index,
  articlesCount,
  page,
  pageSize,
}: GetPageNumberForIndexArgs) => {
  if (!page || !pageSize) {
    return undefined;
  }
  const visiblePages = Math.ceil(articlesCount / pageSize);
  const pageNumber = page - visiblePages + Math.ceil((index + 1) / pageSize);

  return pageNumber !== page ? pageNumber : undefined;
};

export const useRibbonPosition = () => {
  const {
    matches: { matchesDesktop },
  } = useMatchesViewport();

  return matchesDesktop ? RIBBON_POSITION_L : RIBBON_POSITION_S;
};

const getAjustedRelatedSearchPosition = (
  relatedSearchPosition: number,
  hasListViewTeaser?: boolean
) => {
  return hasListViewTeaser && LIST_VIEW_TEASER_POSITION <= relatedSearchPosition
    ? relatedSearchPosition - 1
    : relatedSearchPosition;
};

export const useRelatedSearchPosition = (hasListViewTeaser?: boolean) => {
  const {
    matches: { matchesDesktop, matchesTablet },
  } = useMatchesViewport();

  if (matchesDesktop)
    return getAjustedRelatedSearchPosition(RELATED_SEARCH_POSITION_L, hasListViewTeaser);
  if (matchesTablet)
    return getAjustedRelatedSearchPosition(RELATED_SEARCH_POSITION_M, hasListViewTeaser);

  return getAjustedRelatedSearchPosition(RELATED_SEARCH_POSITION_S, hasListViewTeaser);
};

export const getRibbonPosition = (columnsCount: number) => {
  const columnsLeastCommonMultiplier = leastCommonMultiple(
    columnsCount,
    leastCommonMultiple(COLUMNS_COUNT_S, COLUMNS_COUNT_M)
  );

  return (
    Math.ceil(PREFERRED_RIBBON_POSITION / columnsLeastCommonMultiplier) *
    columnsLeastCommonMultiplier
  );
};

export const getPrevAndNextPage = (page: number) => ({
  prevPage: page === 2 ? '' : page - 1,
  nextPage: page + 1,
});

export const getConstructorAttributes = ({
  isSearchMode,
  totalCount,
  browsedByValue,
  resultId,
  browsedBy,
}: GetCnstrAttrsOptions) => {
  const attrs: StringMap<string | number> = {
    'data-cnstrc-num-results': totalCount,
  };

  if (isSearchMode) {
    attrs['data-cnstrc-search'] = '';
  } else {
    attrs['data-cnstrc-browse'] = '';
    if (browsedBy) {
      attrs['data-cnstrc-filter-name'] = browsedBy;
    }
    attrs['data-cnstrc-filter-value'] = browsedByValue ?? '';
  }

  if (totalCount === 0) {
    attrs['data-cnstrc-zero-result'] = '';
  }

  if (resultId) {
    attrs['data-cnstrc-result-id'] = resultId;
  }

  return attrs;
};

interface GetMinPriceOption {
  prices: {
    regular?: { value?: number | null } | null;
    special?: { value?: number | null } | null;
  };
  variationsMinPrice?: Product['variationsMinPrice'];
}

export const getMinPrice = (article: GetMinPriceOption) => {
  const { prices, variationsMinPrice } = article;
  const currentPrice = prices.special?.value || prices.regular?.value || 0;

  if (variationsMinPrice && currentPrice > variationsMinPrice?.price) {
    return variationsMinPrice.price;
  }

  return null;
};
